import { type ReactNode, useEffect } from "react";

import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgResizableDivider } from "@intelligems/ig-theme/IgResizableDivider";
import { useResizablePanel } from "@intelligems/ig-theme/hooks/use-resizable-panel";
import {
  Box,
  Dialog,
  DialogTitle,
  type SxProps,
  type Theme,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import type { AtlasChatLayout } from "@src/features/ai/utils/atlasCreationPhase";
import { FULL_SCREEN_COLUMN_WIDTH } from "@src/features/ai/components/AiChat/FULL_SCREEN_COLUMN_WIDTH";
import { CHAT_SCROLL_GUTTER_VAR } from "@src/features/ai/components/WorkflowEditor/execution/panel/chatScrollGutter";

interface AiChatOverlayProps {
  atlasCreation?: boolean;
  children: ReactNode;
  fullScreen: boolean;
  /**
   * The inline panel's frame: the resizable right-hand rail, or — for Atlas
   * while it clarifies a new rule — one centred column filling the work area.
   * Ignored by the modal, which is never used for Atlas.
   */
  layout: AtlasChatLayout;
  modal: boolean;
  onClose: () => void;
  onPanelWidthChange?: (width: number) => void;
}

/**
 * Default chat panel width — the panel never sizes to its message content.
 * Figma "chat-panel" (15083:18901): 555px, which leaves the 507px column the
 * Atlas rail's header, messages and dock are drawn at once the 24px side
 * padding below is taken off.
 */
const PANEL_WIDTH = 555;
/**
 * Bounds for the user-resizable desktop rail. Dragged all the way out it
 * reaches the width the same conversation has in full screen, and no wider.
 */
const MIN_PANEL_WIDTH = 400;
const MAX_PANEL_WIDTH = FULL_SCREEN_COLUMN_WIDTH;

const panelSx: SxProps<Theme> = {
  backgroundColor: (theme) => theme.igBackgroundColors.baseLightLow,
  border: 0,
  borderRadius: 0,
  boxSizing: "border-box",
  height: "100%",
  overflowY: "auto",
  // Figma "chat-panel": 8px above the header, 24px at each side, nothing
  // below — the dock's disclaimer is the last thing in the column.
  padding: "8px 24px 0",
  width: "100%",
};

const visuallyHiddenSx = {
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: "1px",
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

const tabletPanelSx: SxProps<Theme> = {
  height: "100%",
  margin: 0,
  marginLeft: "auto",
  maxHeight: "100%",
  maxWidth: PANEL_WIDTH,
  width: `min(${PANEL_WIDTH}px, 100vw)`,
};

export const AiChatOverlay = ({
  atlasCreation = false,
  children,
  fullScreen,
  layout,
  modal,
  onClose,
  onPanelWidthChange,
}: AiChatOverlayProps) => {
  // The rail sits on the right edge of the page, so dragging its left-edge
  // divider away from the panel makes it wider.
  const panel = useResizablePanel({
    initialSize: atlasCreation ? 420 : PANEL_WIDTH,
    minSize: MIN_PANEL_WIDTH,
    maxSize: MAX_PANEL_WIDTH,
    direction: "horizontal",
    position: "right",
    onResize: onPanelWidthChange,
  });
  useEffect(() => {
    onPanelWidthChange?.(panel.size);
  }, [onPanelWidthChange, panel.size]);

  if (modal) {
    return (
      <Dialog
        fullScreen={fullScreen}
        open
        onClose={(_, reason) => {
          if (reason === "escapeKeyDown") {
            onClose();
          }
        }}
        slotProps={{
          paper: {
            autoFocus: true,
            sx: [panelSx, !fullScreen && tabletPanelSx],
            tabIndex: -1,
          },
        }}
      >
        <DialogTitle sx={visuallyHiddenSx}>AI chat</DialogTitle>
        {children}
      </Dialog>
    );
  }

  const isFull = layout === "full";

  // One tree for both layouts: the handle's slot and the column wrapper are
  // always present, so a layout switch restyles the frame and never remounts
  // the chat inside it.
  return (
    <Grid
      container
      sx={{
        flexShrink: 0,
        height: "100%",
        minWidth: 0,
        width: atlasCreation ? "100%" : isFull ? "100%" : panel.size,
      }}
    >
      <IgPaper
        sx={[
          panelSx,
          // Figma "Atlas full screen": the column starts 20px below the top
          // bar. The sides keep the rail's 24px so a narrow window still has
          // a gutter around the 800px column.
          isFull && { paddingTop: "20px" },
          // Docked beside a page, the rail's 16px left padding is the whole
          // gap to the page's card (Figma "rule-detail").
          !isFull && {
            paddingLeft: "16px",
            position: "relative",
            // The transcript's scrollbar sits 10px from the window edge
            // (ENG-7894) rather than inside the rail's 24px side padding:
            // the transcript reaches 14px into that padding and pads its
            // content back, so the messages keep their column.
            [CHAT_SCROLL_GUTTER_VAR]: "14px",
          },
        ]}
      >
        {isFull ? null : (
          // Pinned to the page card's border, not floating in the rail's
          // 16px gap (ENG-7894): centred on the paper's left edge, which is
          // the card's right edge, and spanning the card's height — the rail's
          // 8px top padding to the page column's 10px bottom gap — so the grab
          // bar sits at the card's vertical middle.
          <Box
            sx={{
              bottom: "10px",
              display: "flex",
              left: "-5.5px",
              position: "absolute",
              top: "8px",
              zIndex: 1,
            }}
          >
            <IgResizableDivider
              direction="horizontal"
              onMouseDown={panel.handleMouseDown}
              isResizing={panel.isResizing}
              handleLength={64}
            />
          </Box>
        )}
        <Box
          data-testid="ai-chat-column"
          data-layout={layout}
          sx={{
            height: "100%",
            marginX: "auto",
            maxWidth:
              isFull || atlasCreation ? FULL_SCREEN_COLUMN_WIDTH : "none",
            width: "100%",
          }}
        >
          {children}
        </Box>
      </IgPaper>
    </Grid>
  );
};
